import React, { createContext, ReactElement, useEffect, useState } from 'react';

interface MatrixSortContextType {
  isSortAbsolute: boolean;
  setIsSortAbsolute: (isAbsolute: boolean) => void;
  sortField: string | undefined;
  setSortField: (field: string) => void;
  sortDirection: 'asc' | 'desc' | undefined | null;
  setSortDirection: (direction: 'asc' | 'desc') => void;
}

const MatrixSortContext = createContext<MatrixSortContextType>({
  isSortAbsolute: false,
  setIsSortAbsolute: () => {},
  sortField: undefined,
  setSortField: () => {},
  sortDirection: undefined,
  setSortDirection: () => {},
});

const MatrixSortProvider = ({
  children,
  chartDefinition,
}: {
  children: ReactElement | ReactElement[];
  chartDefinition?: V5ChartDefinition;
}) => {
  const [isSortAbsolute, setIsSortAbsolute] = useState<boolean>(
    () => chartDefinition?.grouping?.currentSort?.isAbsolute ?? false,
  );
  const [sortField, setSortField] = useState<string | undefined>(
    () => chartDefinition?.grouping?.currentSort?.field,
  );
  const [sortDirection, setSortDirection] = useState<
    'asc' | 'desc' | undefined | null
  >(() => chartDefinition?.grouping?.currentSort?.direction);

  // Re-sync sort state when the saved definition changes. The useState
  // initializers above only run on mount, so without this a report gadget
  // (which does not remount on save) keeps feeding the stale sort into its
  // query until a full page refresh. Depend on the primitive currentSort
  // fields and only update on genuine changes so live column-header sorting
  // (which sets these locally and persists the same values) is not clobbered.
  const currentSort = chartDefinition?.grouping?.currentSort;
  const nextIsAbsolute = currentSort?.isAbsolute ?? false;
  const nextField = currentSort?.field;
  const nextDirection = currentSort?.direction;

  useEffect(() => {
    setIsSortAbsolute((prev) =>
      prev !== nextIsAbsolute ? nextIsAbsolute : prev,
    );
    setSortField((prev) => (prev !== nextField ? nextField : prev));
    setSortDirection((prev) => (prev !== nextDirection ? nextDirection : prev));
  }, [nextIsAbsolute, nextField, nextDirection]);

  return (
    <MatrixSortContext.Provider
      value={{
        isSortAbsolute,
        setIsSortAbsolute,
        sortField,
        setSortField,
        setSortDirection,
        sortDirection,
      }}
    >
      {children}
    </MatrixSortContext.Provider>
  );
};

export { MatrixSortContext, MatrixSortProvider };
